import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import api, { mediaSrc, uploadMedia } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Plus, Send, Users, Bot, Trash2, Sparkles, MoreVertical, MessageSquare, Search, UserPlus, X, Loader2, Mic, Volume2, VolumeX, Phone, Paperclip, FileText, Download, Image as ImageIcon, Brain, Check } from 'lucide-react';
import CallScreen from '../components/CallScreen';
import { speak, stopSpeaking } from '../lib/useVoice';

function dedupe(arr) {
  const map = new Map();
  for (const m of arr) map.set(m.message_id, m);
  return Array.from(map.values());
}

function AgentAvatar({ avatar, color, size = 'h-9 w-9' }) {
  return (
    <div className={`${size} rounded-xl grid place-items-center text-lg shrink-0`} style={{ background: (color || '#0e9488') + '22' }}>
      <span>{avatar || '🤖'}</span>
    </div>
  );
}

function MessageAttachments({ attachments, isMe, onSave, savedIds }) {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div className={`flex flex-col gap-1.5 ${isMe ? 'items-end' : 'items-start'}`} data-testid="message-attachments">
      {attachments.map((a) => (
        a.is_image ? (
          <div key={a.media_id} className="relative group">
            <a href={mediaSrc(a.media_id)} target="_blank" rel="noreferrer" className="block">
              <img src={mediaSrc(a.media_id)} alt={a.original_filename} className="max-w-[220px] max-h-[220px] rounded-xl border border-[hsl(var(--border))] object-cover" data-testid={`attachment-image-${a.media_id}`} />
            </a>
            {onSave && (
              <button onClick={() => onSave(a)} disabled={savedIds?.has(a.media_id)} title="Save to Memory"
                className="absolute top-1.5 right-1.5 flex items-center gap-1 rounded-lg bg-[hsl(var(--card))]/90 backdrop-blur px-2 py-1 text-[11px] border border-[hsl(var(--border))] shadow-sm hover:bg-[hsl(var(--card))] transition-colors disabled:opacity-100" data-testid={`save-to-memory-${a.media_id}`}>
                {savedIds?.has(a.media_id) ? (<><Check className="h-3 w-3" style={{ color: 'hsl(var(--success))' }} /> Saved</>) : (<><Brain className="h-3 w-3" style={{ color: 'hsl(var(--primary))' }} /> Save</>)}
              </button>
            )}
          </div>
        ) : (
          <div key={a.media_id} className="flex items-center gap-1.5">
            <a href={mediaSrc(a.media_id)} target="_blank" rel="noreferrer" download={a.original_filename}
               className="flex items-center gap-2 rounded-xl border border-[hsl(var(--border))] bg-card px-3 py-2 text-sm hover:bg-[hsl(var(--secondary))] transition-colors max-w-[240px]" data-testid={`attachment-file-${a.media_id}`}>
              <FileText className="h-4 w-4 shrink-0" style={{ color: 'hsl(var(--primary))' }} />
              <span className="truncate flex-1">{a.original_filename}</span>
              <Download className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--muted-foreground))]" />
            </a>
            {onSave && (
              <button onClick={() => onSave(a)} disabled={savedIds?.has(a.media_id)} title="Save to Memory"
                className="flex items-center gap-1 rounded-lg border border-[hsl(var(--border))] bg-card px-2 py-2 text-[11px] hover:bg-[hsl(var(--secondary))] transition-colors shrink-0 disabled:opacity-100" data-testid={`save-to-memory-${a.media_id}`}>
                {savedIds?.has(a.media_id) ? (<><Check className="h-3.5 w-3.5" style={{ color: 'hsl(var(--success))' }} /> Saved</>) : (<><Brain className="h-3.5 w-3.5" style={{ color: 'hsl(var(--primary))' }} /> Save</>)}
              </button>
            )}
          </div>
        )
      ))}
    </div>
  );
}

function NewChatDialog({ agents, onCreated, testId = 'new-chat-button' }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('direct');
  const [selected, setSelected] = useState([]);
  const [emails, setEmails] = useState([]);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  const toggle = (id) => {
    if (type === 'direct') setSelected([id]);
    else setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  };

  const searchUsers = async (q) => {
    setQuery(q);
    if (!q) { setResults([]); return; }
    try { const res = await api.get(`/users/search?q=${encodeURIComponent(q)}`); setResults(res.data); } catch (e) {}
  };

  const create = async () => {
    if (selected.length === 0) { toast.error('Pick at least one agent'); return; }
    setBusy(true);
    try {
      const res = await api.post('/chats', { type, agent_ids: selected, user_emails: emails });
      onCreated(res.data);
      setOpen(false); setSelected([]); setEmails([]); setType('direct');
      toast.success('Chat created');
    } catch (e) { toast.error('Could not create chat'); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="rounded-xl gap-1.5" style={{ background: 'hsl(var(--primary))' }} data-testid={testId}><Plus className="h-4 w-4" /> New</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Start a conversation</DialogTitle>
          <DialogDescription>Chat 1:1 with an agent, or build a group with people and agents.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 p-1 bg-[hsl(var(--secondary))] rounded-xl">
          <button onClick={() => { setType('direct'); setSelected([]); }} className={`flex-1 rounded-lg py-2 text-sm font-500 transition-colors ${type === 'direct' ? 'bg-card shadow-sm' : 'text-[hsl(var(--muted-foreground))]'}`} data-testid="chat-type-direct"><Bot className="h-4 w-4 inline mr-1.5" />1:1 with agent</button>
          <button onClick={() => setType('group')} className={`flex-1 rounded-lg py-2 text-sm font-500 transition-colors ${type === 'group' ? 'bg-card shadow-sm' : 'text-[hsl(var(--muted-foreground))]'}`} data-testid="chat-type-group"><Users className="h-4 w-4 inline mr-1.5" />Group</button>
        </div>
        <div>
          <p className="text-xs font-600 text-[hsl(var(--muted-foreground))] mb-2">SELECT AGENTS</p>
          <div className="max-h-52 overflow-y-auto space-y-1.5 pr-1">
            {agents.map((a) => (
              <button key={a.agent_id} onClick={() => toggle(a.agent_id)} className={`w-full flex items-center gap-2.5 rounded-xl p-2 border transition-colors ${selected.includes(a.agent_id) ? 'border-[hsl(var(--primary))] bg-[hsl(var(--accent))]/40' : 'border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]'}`} data-testid={`select-agent-${a.agent_id}`}>
                <AgentAvatar avatar={a.avatar} color={a.color} size="h-8 w-8" />
                <div className="text-left min-w-0"><p className="text-sm font-600 truncate">{a.name}</p><p className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">{a.description}</p></div>
                {selected.includes(a.agent_id) && <div className="ml-auto h-2 w-2 rounded-full" style={{ background: 'hsl(var(--primary))' }} />}
              </button>
            ))}
          </div>
        </div>
        {type === 'group' && (
          <div>
            <p className="text-xs font-600 text-[hsl(var(--muted-foreground))] mb-2">INVITE PEOPLE (optional)</p>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-[hsl(var(--muted-foreground))]" />
              <Input value={query} onChange={(e) => searchUsers(e.target.value)} placeholder="Search by name or email" className="h-10 rounded-xl pl-9" data-testid="invite-search-input" />
            </div>
            {results.length > 0 && (
              <div className="mt-2 space-y-1">
                {results.map((u) => (
                  <button key={u.user_id} onClick={() => { if (!emails.includes(u.email)) setEmails([...emails, u.email]); setResults([]); setQuery(''); }} className="w-full flex items-center gap-2 rounded-lg p-2 hover:bg-[hsl(var(--secondary))] text-left">
                    <UserPlus className="h-4 w-4" /><span className="text-sm">{u.name} · {u.email}</span>
                  </button>
                ))}
              </div>
            )}
            {emails.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {emails.map((e) => <Badge key={e} variant="secondary" className="gap-1">{e}<button onClick={() => setEmails(emails.filter((x) => x !== e))}><X className="h-3 w-3" /></button></Badge>)}
              </div>
            )}
            <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-2">Try demo teammates: alex@teamily.app, sam@teamily.app</p>
          </div>
        )}
        <DialogFooter>
          <Button onClick={create} disabled={busy} className="rounded-xl" style={{ background: 'hsl(var(--primary))' }} data-testid="create-chat-confirm">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create chat'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ChatsPage() {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [chats, setChats] = useState([]);
  const [agents, setAgents] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [taskSuggestion, setTaskSuggestion] = useState(null);
  const [sending, setSending] = useState(false);
  const [autoSpeak, setAutoSpeak] = useState(false);
  const [listening, setListening] = useState(false);
  const [callOpen, setCallOpen] = useState(false);
  const [pending, setPending] = useState([]); // [{ tmpId, name, uploading, media }]
  const [speakingId, setSpeakingId] = useState(null);
  const [ttsLoadingId, setTtsLoadingId] = useState(null);
  const [imgGen, setImgGen] = useState(false);
  const [sttBusy, setSttBusy] = useState(false);
  const [savedIds, setSavedIds] = useState(() => new Set());
  const recRef = useRef(null);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const audioRef = useRef(null);
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);

  const loadChats = useCallback(async () => {
    const res = await api.get('/chats');
    setChats(res.data);
    return res.data;
  }, []);

  const loadAgents = useCallback(async () => {
    const res = await api.get('/agents');
    setAgents(res.data);
  }, []);

  const loadMessages = useCallback(async (id) => {
    try { const res = await api.get(`/chats/${id}/messages`); setMessages(dedupe(res.data)); } catch (e) {}
  }, []);

  useEffect(() => { loadChats(); loadAgents(); }, [loadChats, loadAgents]);

  useEffect(() => {
    if (!chatId) { setActive(null); setMessages([]); return; }
    (async () => {
      try {
        const res = await api.get(`/chats/${chatId}`);
        setActive(res.data);
        loadMessages(chatId);
      } catch (e) { navigate('/app/chats'); }
    })();
  }, [chatId, loadMessages, navigate]);

  // near-real-time polling for group chats
  useEffect(() => {
    if (!active || active.type !== 'group') return;
    const t = setInterval(() => loadMessages(active.chat_id), 4000);
    return () => clearInterval(t);
  }, [active, loadMessages]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const openChat = (c) => navigate(`/app/chats/${c.chat_id}`);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (fileRef.current) fileRef.current.value = '';
    if (!files.length || !active) return;
    for (const file of files) {
      if (file.size > 25 * 1024 * 1024) { toast.error(`${file.name} is over 25 MB`); continue; }
      const tmpId = 'att-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7);
      setPending((p) => [...p, { tmpId, name: file.name, uploading: true, media: null }]);
      try {
        const media = await uploadMedia(file, 'chat', active.chat_id);
        setPending((p) => p.map((x) => x.tmpId === tmpId ? { ...x, uploading: false, media } : x));
      } catch (err) {
        toast.error(`Couldn't upload ${file.name}`);
        setPending((p) => p.filter((x) => x.tmpId !== tmpId));
      }
    }
  };

  const removePending = (tmpId) => setPending((p) => p.filter((x) => x.tmpId !== tmpId));

  const send = async () => {
    if (!active || sending) return;
    const ready = pending.filter((p) => p.media);
    const stillUploading = pending.some((p) => p.uploading);
    if (stillUploading) { toast.error('Hang on — files are still uploading'); return; }
    const text = input.trim();
    if (!text && ready.length === 0) return;
    const attachmentIds = ready.map((p) => p.media.media_id);
    const attachmentPreview = ready.map((p) => p.media);
    setInput('');
    setPending([]);
    const optimistic = { message_id: 'tmp-' + Date.now(), role: 'user', sender_type: 'human', sender_id: user?.user_id, sender_name: user?.name, content: text, attachments: attachmentPreview };
    setMessages((m) => [...m, optimistic]);
    setSending(true);
    try {
      const res = await api.post(`/chats/${active.chat_id}/messages`, { content: text, attachments: attachmentIds });
      setMessages((m) => dedupe([...m.filter((x) => x.message_id !== optimistic.message_id), res.data.user_message, ...res.data.replies]));
      setTaskSuggestion(res.data.task_suggestion || null);
      if (autoSpeak && res.data.replies?.[0]?.content) {
        const r0 = res.data.replies[0];
        const speaker = active.agents?.find((a) => a.agent_id === r0.sender_id) || active.agents?.[0];
        speak(r0.content, { voice: speaker?.voice, rate: speaker?.voice_rate });
      }
      loadChats();
    } catch (e) {
      toast.error('Failed to send');
      setMessages((m) => m.filter((x) => x.message_id !== optimistic.message_id));
      setInput(text);
    } finally { setSending(false); }
  };

  const addSuggestedTask = async () => {
    if (!taskSuggestion) return;
    try {
      await api.post('/tasks', { title: taskSuggestion.title, status: 'todo', auto_classify: true });
      toast.success('Added to today', { description: taskSuggestion.title });
      setTaskSuggestion(null);
    } catch (e) { toast.error('Could not add task'); }
  };

  const deleteChat = async (id) => {
    await api.delete(`/chats/${id}`);
    await loadChats();
    if (active?.chat_id === id) navigate('/app/chats');
    toast.success('Chat deleted');
  };

  const toggleMic = async () => {
    if (listening) {
      try { mediaRecRef.current && mediaRecRef.current.stop(); } catch (e) {}
      setListening(false);
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      toast.error('Microphone not available in this browser');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        if (!blob.size) return;
        setSttBusy(true);
        try {
          const fd = new FormData();
          fd.append('file', blob, 'dictation.webm');
          const res = await api.post('/voice/stt', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          const text = (res.data?.text || '').trim();
          if (text) setInput((cur) => (cur ? cur + ' ' : '') + text);
          else toast.error("Didn't catch that — try again");
        } catch (e) { toast.error('Could not transcribe audio'); }
        finally { setSttBusy(false); }
      };
      mediaRecRef.current = rec;
      rec.start();
      setListening(true);
    } catch (e) { toast.error('Microphone permission denied'); }
  };


  const askAI = async (content) => {
    toast.loading('Asking your AI…', { id: 'ask' });
    try {
      const res = await api.post(`/chats/${active.chat_id}/ask`, { content: `Explain / expand on this: "${content}"` });
      toast.success(res.data.reply.slice(0, 180), { id: 'ask', duration: 8000 });
    } catch (e) { toast.error('Could not ask AI', { id: 'ask' }); }
  };

  const stopAudio = () => {
    if (audioRef.current) { try { audioRef.current.pause(); } catch (e) {} audioRef.current = null; }
    setSpeakingId(null);
  };

  const playMessage = async (m) => {
    if (speakingId === m.message_id) { stopAudio(); return; }
    stopAudio();
    setTtsLoadingId(m.message_id);
    try {
      const res = await api.post('/voice/tts', { text: m.content }, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setSpeakingId(null); URL.revokeObjectURL(url); };
      audio.onerror = () => { setSpeakingId(null); URL.revokeObjectURL(url); };
      setSpeakingId(m.message_id);
      await audio.play();
    } catch (e) { toast.error('Could not read this aloud'); }
    finally { setTtsLoadingId(null); }
  };

  const generateImage = async () => {
    const prompt = input.trim();
    if (!prompt) { toast.error('Type what you want to picture, then tap the image button'); return; }
    if (!active || imgGen) return;
    setImgGen(true);
    setInput('');
    toast.loading('Painting your image…', { id: 'imggen' });
    try {
      const res = await api.post('/image/generate', { prompt, chat_id: active.chat_id });
      if (res.data.message) setMessages((mm) => dedupe([...mm, res.data.message]));
      loadChats();
      toast.success('Image ready', { id: 'imggen' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Image generation failed', { id: 'imggen' });
      setInput(prompt);
    } finally { setImgGen(false); }
  };

  useEffect(() => () => stopAudio(), []);

  const saveToMemory = async (a) => {
    try {
      await api.post('/memory/from-media', { media_id: a.media_id, title: a.original_filename });
      setSavedIds((prev) => { const n = new Set(prev); n.add(a.media_id); return n; });
      toast.success('Saved to Universal Memory');
    } catch (e) { toast.error('Could not save to memory'); }
  };

  return (
    <div className="h-full grid grid-cols-1 lg:grid-cols-[300px_1fr] min-h-0">
      {/* Chat list */}
      <div className={`border-r border-[hsl(var(--border))] bg-[hsl(var(--surface-1))] flex flex-col min-h-0 ${chatId ? 'hidden lg:flex' : 'flex'}`}>
        <div className="flex items-center justify-between px-4 py-3.5 border-b border-[hsl(var(--border))]">
          <h2 className="font-display font-700 text-lg">Chats</h2>
          <NewChatDialog agents={agents} onCreated={(c) => { setChats((cs) => [c, ...cs]); navigate(`/app/chats/${c.chat_id}`); }} />
        </div>
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            {chats.length === 0 && (
              <div className="text-center py-14 px-6">
                <div className="h-12 w-12 rounded-2xl mx-auto grid place-items-center mb-3" style={{ background: 'hsl(var(--accent))' }}><MessageSquare className="h-6 w-6" style={{ color: 'hsl(var(--primary))' }} /></div>
                <p className="text-sm font-600">No chats yet</p>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Start a 1:1 with your Personal AI.</p>
              </div>
            )}
            {chats.map((c) => (
              <button key={c.chat_id} onClick={() => openChat(c)} className={`w-full flex items-center gap-3 rounded-xl p-2.5 text-left transition-colors ${active?.chat_id === c.chat_id ? 'bg-[hsl(var(--accent))]/50' : 'hover:bg-[hsl(var(--secondary))]'}`} data-testid={`chat-item-${c.chat_id}`}>
                {c.type === 'group' ? (
                  <div className="h-9 w-9 rounded-xl grid place-items-center shrink-0" style={{ background: 'hsl(var(--brand-sky) / 0.15)' }}><Users className="h-4 w-4" style={{ color: 'hsl(var(--brand-sky))' }} /></div>
                ) : (
                  <AgentAvatar avatar={c.agents?.[0]?.avatar} color={c.agents?.[0]?.color} />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-600 truncate">{c.title}</p>
                  <p className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">{c.last_message ? `${c.last_message.sender_name}: ${c.last_message.content}` : (c.type === 'group' ? `${c.members?.length || 1} people · ${c.agents?.length || 0} agents` : 'Say hello')}</p>
                </div>
              </button>
            ))}
          </div>
        </ScrollArea>
      </div>

      {/* Thread */}
      <div className="flex flex-col min-h-0 h-full">
        {!active ? (
          <div className="flex-1 grid place-items-center p-8">
            <div className="text-center max-w-sm">
              <div className="h-16 w-16 rounded-3xl mx-auto grid place-items-center mb-4" style={{ background: 'hsl(var(--accent))' }}><Sparkles className="h-8 w-8" style={{ color: 'hsl(var(--primary))' }} /></div>
              <h3 className="font-display text-xl font-700">Your Human+AI workspace</h3>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">Pick a chat on the left, or start a new one. Your Personal AI already knows what you've told it.</p>
              <div className="mt-5"><NewChatDialog agents={agents} testId="new-chat-empty-button" onCreated={(c) => { setChats((cs) => [c, ...cs]); navigate(`/app/chats/${c.chat_id}`); }} /></div>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 px-4 py-3 border-b border-[hsl(var(--border))] bg-[hsl(var(--surface-1))]">
              {active.type === 'group' ? (
                <div className="h-9 w-9 rounded-xl grid place-items-center" style={{ background: 'hsl(var(--brand-sky) / 0.15)' }}><Users className="h-4 w-4" style={{ color: 'hsl(var(--brand-sky))' }} /></div>
              ) : <AgentAvatar avatar={active.agents?.[0]?.avatar} color={active.agents?.[0]?.color} />}
              <div className="min-w-0">
                <p className="font-600 truncate">{active.title}</p>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {active.agents?.map((a) => <Badge key={a.agent_id} variant="secondary" className="text-[10px] gap-1"><span className="h-1.5 w-1.5 rounded-full" style={{ background: 'hsl(var(--brand-mint))' }} />{a.name}</Badge>)}
                  {active.type === 'group' && active.members?.filter((m) => m.user_id !== user?.user_id).map((m) => <Badge key={m.user_id} variant="outline" className="text-[10px]">{m.name}</Badge>)}
                </div>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="ml-auto rounded-xl" data-testid="chat-menu-button"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end"><DropdownMenuItem onClick={() => deleteChat(active.chat_id)} className="text-[hsl(var(--destructive))]"><Trash2 className="h-4 w-4 mr-2" /> Delete chat</DropdownMenuItem></DropdownMenuContent>
              </DropdownMenu>
              <Button variant="ghost" size="icon" className={`rounded-xl ${autoSpeak ? 'text-[hsl(var(--primary))]' : ''}`} onClick={() => { setAutoSpeak(!autoSpeak); if (autoSpeak) stopSpeaking(); }} title="Read replies aloud" data-testid="chat-speak-toggle">{autoSpeak ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}</Button>
              {active.type === 'direct' && active.agents?.[0] && (
                <Button variant="ghost" size="icon" className="rounded-xl" onClick={() => setCallOpen(true)} title="Voice call" data-testid="chat-call-button"><Phone className="h-4 w-4" /></Button>
              )}
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-5 space-y-4" role="log" aria-live="polite" data-testid="messages-container">
              {messages.map((m) => {
                const isMe = m.sender_type === 'human' && m.sender_id === user?.user_id;
                const isAgent = m.sender_type === 'agent';
                return (
                  <motion.div key={m.message_id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} className={`flex gap-2.5 ${isMe ? 'justify-end' : 'justify-start'}`}>
                    {!isMe && (isAgent ? <AgentAvatar avatar={m.avatar} color={m.color} size="h-8 w-8" /> : <Avatar className="h-8 w-8"><AvatarFallback className="text-xs" style={{ background: 'hsl(var(--secondary))' }}>{(m.sender_name || 'U')[0]}</AvatarFallback></Avatar>)}
                    <div className={`max-w-[80%] ${isMe ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                      {!isMe && <span className="text-[11px] text-[hsl(var(--muted-foreground))] px-1 flex items-center gap-1">{m.sender_name}{isAgent && <Badge variant="secondary" className="text-[9px] py-0 px-1">AI</Badge>}</span>}
                      {m.content ? (
                        <div className={`rounded-2xl px-4 py-2.5 text-sm ${isMe ? 'text-white' : isAgent ? 'bg-card border border-[hsl(var(--border))] shadow-sm' : 'bg-[hsl(var(--secondary))]'}`} style={isMe ? { background: 'hsl(var(--primary))' } : {}}>
                          {isMe ? <p className="whitespace-pre-wrap">{m.content}</p> : <div className="prose-chat"><ReactMarkdown>{m.content}</ReactMarkdown></div>}
                        </div>
                      ) : null}
                      <MessageAttachments attachments={m.attachments} isMe={isMe} onSave={saveToMemory} savedIds={savedIds} />
                      {isAgent && m.content && (
                        <div className="flex items-center gap-3 px-1">
                          <button onClick={() => playMessage(m)} className="text-[11px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] flex items-center gap-1 transition-colors" data-testid={`play-message-${m.message_id}`}>
                            {ttsLoadingId === m.message_id ? <Loader2 className="h-3 w-3 animate-spin" /> : speakingId === m.message_id ? <VolumeX className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
                            {speakingId === m.message_id ? 'Stop' : 'Play'}
                          </button>
                          {active.type === 'direct' && (
                            <button onClick={() => askAI(m.content)} className="text-[11px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] flex items-center gap-1 transition-colors" data-testid="ask-ai-button"><Sparkles className="h-3 w-3" /> Ask AI to expand</button>
                          )}
                        </div>
                      )}
                    </div>
                    {isMe && <Avatar className="h-8 w-8">{(user?.avatar_media_id || user?.picture) ? <AvatarImage src={user?.avatar_media_id ? mediaSrc(user.avatar_media_id) : user.picture} /> : null}<AvatarFallback className="text-xs" style={{ background: 'hsl(var(--accent))' }}>{(user?.name || 'U')[0]}</AvatarFallback></Avatar>}
                  </motion.div>
                );
              })}
              {sending && (
                <div className="flex gap-2.5">
                  <AgentAvatar avatar={active.agents?.[0]?.avatar} color={active.agents?.[0]?.color} size="h-8 w-8" />
                  <div className="rounded-2xl px-4 py-3 bg-card border border-[hsl(var(--border))] shadow-sm flex gap-1 items-center"><span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" /></div>
                </div>
              )}
            </div>

            <div className="p-3 border-t border-[hsl(var(--border))] bg-[hsl(var(--surface-1))]">
              {active.type === 'group' && active.agents?.length > 1 && (
                <p className="text-[11px] text-[hsl(var(--muted-foreground))] px-1 mb-1.5">Tip: @mention an agent (e.g. @{active.agents[0].name.split(' ')[0]}) to direct your question.</p>
              )}
              {pending.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2" data-testid="chat-pending-attachments">
                  {pending.map((p) => (
                    <div key={p.tmpId} className="flex items-center gap-1.5 rounded-lg border border-[hsl(var(--border))] bg-card pl-2 pr-1 py-1 text-xs">
                      {p.uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" style={{ color: 'hsl(var(--primary))' }} />}
                      <span className="truncate max-w-[140px]">{p.name}</span>
                      <button onClick={() => removePending(p.tmpId)} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))]" data-testid="remove-attachment"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}
              <input ref={fileRef} type="file" multiple className="hidden" onChange={handleFiles} data-testid="chat-file-input" />
              {taskSuggestion && (
                <div className="mb-2 flex items-center gap-2 rounded-xl border px-3 py-2" style={{ borderColor: 'hsl(var(--primary) / 0.4)', background: 'hsl(var(--primary) / 0.06)' }} data-testid="chat-task-suggestion">
                  <Sparkles className="h-4 w-4 shrink-0" style={{ color: 'hsl(var(--primary))' }} />
                  <span className="text-[13px] min-w-0 flex-1 truncate">Spotted a to-do: <span className="font-600">{taskSuggestion.title}</span></span>
                  <Button size="sm" className="h-7 rounded-lg text-xs gap-1 text-white shrink-0" style={{ background: 'hsl(var(--primary))' }} onClick={addSuggestedTask} data-testid="chat-add-task-button"><Plus className="h-3.5 w-3.5" />Add to today</Button>
                  <button onClick={() => setTaskSuggestion(null)} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] shrink-0" data-testid="chat-dismiss-suggestion"><X className="h-4 w-4" /></button>
                </div>
              )}
              <div className="flex items-end gap-2">
                <Button onClick={() => fileRef.current?.click()} size="icon" variant="secondary" className="h-11 w-11 rounded-2xl shrink-0 border border-[hsl(var(--border))]" title="Attach file" data-testid="chat-attach-button"><Paperclip className="h-4 w-4" /></Button>
                <Button onClick={generateImage} disabled={imgGen} size="icon" variant="secondary" className="h-11 w-11 rounded-2xl shrink-0 border border-[hsl(var(--border))]" title="Generate an image from your text" data-testid="chat-image-button">{imgGen ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}</Button>
                <Textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="Message your team… (Enter to send)" className="min-h-[44px] max-h-[160px] rounded-2xl resize-none" data-testid="chat-composer-textarea" />
                <Button onClick={toggleMic} size="icon" variant="secondary" className={`h-11 w-11 rounded-2xl shrink-0 border border-[hsl(var(--border))] ${listening ? 'text-[hsl(var(--destructive))] animate-pulse' : ''}`} disabled={sttBusy} title={listening ? 'Stop & transcribe' : 'Dictate'} data-testid="chat-mic-button">{sttBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic className="h-4 w-4" />}</Button>
                <Button onClick={send} disabled={sending || (!input.trim() && pending.length === 0)} size="icon" className="h-11 w-11 rounded-2xl shrink-0" style={{ background: 'hsl(var(--primary))' }} data-testid="chat-send-button"><Send className="h-4 w-4" /></Button>
              </div>
            </div>
          </>
        )}
      </div>
      {callOpen && active?.type === 'direct' && active.agents?.[0] && (
        <CallScreen chat={active} agent={active.agents[0]} onExchange={() => loadMessages(active.chat_id)} onClose={() => { setCallOpen(false); loadMessages(active.chat_id); }} />
      )}
    </div>
  );
}
